import { useMemo, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { SIDEBAR_PREVIEW_ITEMS } from "../../../../utils/constants/permissionsConstants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { FEATURE_GUARD_ALLOWED_ENVIRONMENTS } from "../../../../utils/Constants";
import {
  DEV_ONLY_PERMISSION_KEYS,
  SIDEBAR_ICONS as SIDEBAR_ICONS_CONSTANT,
} from "../constants";
import "./SidebarPreview.css";

import SportsbookIcon from "../../../../assets/icons/ic_sportsbook.svg";

const SIDEBAR_ICONS = {
  ...SIDEBAR_ICONS_CONSTANT,
  sportsbook_2: SportsbookIcon,
};

const DEV_ONLY_PREVIEW_KEY_SET = new Set(DEV_ONLY_PERMISSION_KEYS);

/**
 * Matches `PermissionCollapse` / `FeatureFlagGuard` `developmentMode`: show dev-only
 * permission keys only when `AppStrings.PAM_APP_NAME` includes an allow-list token.
 * @param {Array<{ id: string, label: string, children?: unknown[] }>} nodes
 * @param {boolean} isDevAppEnvironment
 */
function filterDevOnlyFromPreviewTree(nodes, isDevAppEnvironment) {
  if (isDevAppEnvironment) {
    return nodes;
  }
  if (!nodes?.length) {
    return [];
  }
  return nodes
    .filter((n) => !DEV_ONLY_PREVIEW_KEY_SET.has(n.id))
    .map((n) => ({
      ...n,
      children: filterDevOnlyFromPreviewTree(
        n.children ?? [],
        isDevAppEnvironment,
      ),
    }));
}

/**
 * True if this node or any descendant has `permissions[id] === true`.
 * @param {{ id: string, label: string, children?: unknown[] }} node
 * @param {Record<string, boolean>} permissions
 */
function hasSubtreeAccess(node, permissions) {
  if (!node) return false;
  if (Boolean(permissions[node.id])) return true;
  return (node.children ?? []).some((child) =>
    hasSubtreeAccess(child, permissions),
  );
}

/**
 * True when every leaf under this node has `permissions[id] === true`. Paired
 * with `hasSubtreeAccess` to drive the default-expanded rule: a branch is
 * expanded by default only when it is *partially* granted — fully-granted
 * (`hasAll`) and fully-off (`!hasAny`) branches both stay collapsed so the
 * preview stays scannable.
 * @param {{ id: string, label: string, children?: unknown[] }} node
 * @param {Record<string, boolean>} permissions
 */
function hasAllSubtreeAccess(node, permissions) {
  if (!node) return false;
  const children = node.children ?? [];
  if (!children.length) return Boolean(permissions[node.id]);
  return children.every((child) => hasAllSubtreeAccess(child, permissions));
}

/**
 * Walk the group tree and produce the default expanded state per expandable
 * node id: `true` only when the subtree is partially granted (some but not
 * all leaves on). Fully-granted branches carry a check indicator, fully-off
 * branches carry an X — neither benefits from opening automatically. Leaves
 * are omitted (they have no expanded state to control).
 * @param {Array<{ id: string, children?: unknown[] }>} nodes
 * @param {Record<string, boolean>} permissions
 */
function buildDefaultExpanded(nodes, permissions) {
  const acc = {};
  const walk = (list) => {
    for (const node of list ?? []) {
      if (node.children?.length) {
        const hasAny = hasSubtreeAccess(node, permissions);
        const hasAll = hasAllSubtreeAccess(node, permissions);
        acc[node.id] = hasAny && !hasAll;
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return acc;
}

/** @param {{ key: string, display_name?: string, items?: unknown[] }} item */
function mapSchemaItemToNode(item) {
  const rawChildren = item.items ?? [];
  const children = rawChildren.length
    ? rawChildren.map(mapSchemaItemToNode)
    : [];
  return {
    id: item.key,
    label: item.display_name ?? item.key,
    children,
  };
}

function buildGroupsFromSchema(availablePermissions) {
  if (!availablePermissions?.length) return null;
  return availablePermissions.map((cat) => ({
    id: cat.category_key ?? cat.display_name,
    label: cat.display_name ?? cat.category,
    children: (cat.items ?? []).map(mapSchemaItemToNode),
  }));
}

function buildGroupsFromConstants() {
  const groups = [];
  let current = null;
  SIDEBAR_PREVIEW_ITEMS.forEach((item) => {
    if (!item.isSubItem) {
      current = { id: item.id, label: item.label, children: [] };
      groups.push(current);
    } else if (current) {
      current.children.push({ id: item.id, label: item.label, children: [] });
    }
  });
  return groups;
}

/** Stable fallback when `availablePermissions` is missing (avoid new array each render). */
const FALLBACK_SIDEBAR_PREVIEW_GROUPS = buildGroupsFromConstants();

function resolveGroupIconSrc(groupId) {
  const k = String(groupId).toLowerCase();
  if (SIDEBAR_ICONS[k]) return SIDEBAR_ICONS[k];
  if (k.startsWith("sportsbook")) return SIDEBAR_ICONS.sportsbook;
  return null;
}

/**
 * Renders nested permission rows (schema: items with optional nested `items`).
 */
function PreviewTree({ nodes, depth, expanded, onToggle, permissions }) {
  if (!nodes?.length) return null;

  const depthClass =
    depth >= 3
      ? "sidebar-preview-row--depth-3"
      : depth === 2
        ? "sidebar-preview-row--depth-2"
        : "sidebar-preview-row--depth-1";

  return (
    <>
      {nodes.map((node) => {
        const hasKids = Boolean(node.children?.length);
        const isOpen = Boolean(expanded[node.id]);
        const nodeAccess = hasKids
          ? hasSubtreeAccess(node, permissions)
          : Boolean(permissions[node.id]);

        if (!hasKids) {
          return (
            <div
              key={node.id}
              className={[
                "sidebar-preview-row",
                "sidebar-preview-row--child",
                depthClass,
                !nodeAccess ? "sidebar-preview-row--no-access" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="sidebar-preview-row-icon">
                <span className="sidebar-preview-dot" />
              </span>
              <span className="sidebar-preview-row-label">{node.label}</span>
              <span className="sidebar-preview-row-indicator">
                {nodeAccess ? (
                  <CheckOutlined
                    className="sidebar-preview-check"
                    aria-hidden
                  />
                ) : (
                  <CloseOutlined className="sidebar-preview-x" aria-hidden />
                )}
              </span>
            </div>
          );
        }

        return (
          <div key={node.id} className="sidebar-preview-subtree">
            <div
              className={[
                "sidebar-preview-row",
                "sidebar-preview-row--child",
                "sidebar-preview-row--expandable",
                depthClass,
                !nodeAccess ? "sidebar-preview-row--no-access" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onToggle(node.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle(node.id);
                }
              }}
            >
              <span className="sidebar-preview-row-icon">
                <span className="sidebar-preview-dot" />
              </span>
              <span className="sidebar-preview-row-label">{node.label}</span>
              <span className="sidebar-preview-caret-wrap">
                {isOpen ? (
                  <DownOutlined className="sidebar-preview-caret" />
                ) : (
                  <RightOutlined className="sidebar-preview-caret" />
                )}
              </span>
              <span className="sidebar-preview-row-indicator">
                {nodeAccess ? (
                  <CheckOutlined
                    className="sidebar-preview-check"
                    aria-hidden
                  />
                ) : (
                  <CloseOutlined className="sidebar-preview-x" aria-hidden />
                )}
              </span>
            </div>
            {isOpen ? (
              <PreviewTree
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                permissions={permissions}
              />
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function SidebarPreview({ permissions = {}, availablePermissions }) {
  const isDevAppEnvironment = FEATURE_GUARD_ALLOWED_ENVIRONMENTS.some((env) =>
    (AppStrings?.PAM_APP_NAME ?? "").includes(env),
  );

  const groups = useMemo(() => {
    const fromSchema = buildGroupsFromSchema(availablePermissions);
    const raw = fromSchema ?? FALLBACK_SIDEBAR_PREVIEW_GROUPS;
    return filterDevOnlyFromPreviewTree(raw, isDevAppEnvironment);
  }, [availablePermissions, isDevAppEnvironment]);

  // Only *partially* granted branches expand by default. Fully-granted and
  // fully-off branches both collapse — the indicator (check / X) already
  // conveys the state, and keeping unactionable subtrees closed keeps the
  // preview scannable. Recomputed whenever `groups` or `permissions` change
  // so late-arriving schema data (or a permissions edit) picks the right
  // initial state instead of leaving newly-visible nodes stuck on stale
  // defaults.
  const defaultExpanded = useMemo(
    () => buildDefaultExpanded(groups, permissions),
    [groups, permissions],
  );

  // User-driven overrides layered on top of the default. Explicit toggles win
  // over the derived default so a click never gets snapped back on the next
  // permissions change.
  const [overrides, setOverrides] = useState({});

  const expanded = useMemo(
    () => ({ ...defaultExpanded, ...overrides }),
    [defaultExpanded, overrides],
  );

  const toggle = (id) => {
    // Read from the merged view so the flip is against what the user actually
    // sees — flipping only the override map would toggle relative to `false`
    // even when the default has already opened the node.
    setOverrides((prev) => {
      const currentlyOpen = Object.prototype.hasOwnProperty.call(prev, id)
        ? prev[id]
        : Boolean(defaultExpanded[id]);
      return { ...prev, [id]: !currentlyOpen };
    });
  };

  const hasAccess = (id) => Boolean(permissions[id]);

  const hasGroupAccess = (group) => {
    if (hasAccess(group.id)) return true;
    if (group.children?.length) {
      return group.children.some((child) =>
        hasSubtreeAccess(child, permissions),
      );
    }
    return false;
  };

  if (!groups?.length) return null;

  return (
    <div className="sidebar-preview" data-testid="sidebar-preview">
      <div className="sidebar-preview-list">
        {groups.map((group) => {
          const isExpanded = expanded[group.id];
          const hasChildren = (group.children?.length ?? 0) > 0;
          const groupAccess = hasGroupAccess(group);
          const iconSrc = resolveGroupIconSrc(group.id);
          return (
            <div key={group.id} className="sidebar-preview-group">
              <div
                className={`sidebar-preview-row ${!groupAccess ? "sidebar-preview-row--no-access" : ""}`}
                onClick={() => hasChildren && toggle(group.id)}
              >
                <span className="sidebar-preview-row-icon">
                  {iconSrc ? (
                    <img
                      src={iconSrc}
                      alt=""
                      className="sidebar-preview-icon-svg"
                    />
                  ) : (
                    <span className="sidebar-preview-dot" />
                  )}
                </span>
                <span className="sidebar-preview-row-label">{group.label}</span>
                {hasChildren && (
                  <span className="sidebar-preview-caret-wrap">
                    {isExpanded ? (
                      <DownOutlined className="sidebar-preview-caret" />
                    ) : (
                      <RightOutlined className="sidebar-preview-caret" />
                    )}
                  </span>
                )}
                <span className="sidebar-preview-row-indicator">
                  {groupAccess ? (
                    <CheckOutlined
                      className="sidebar-preview-check"
                      aria-hidden
                    />
                  ) : (
                    <CloseOutlined className="sidebar-preview-x" aria-hidden />
                  )}
                </span>
              </div>
              {hasChildren && isExpanded ? (
                <PreviewTree
                  nodes={group.children}
                  depth={1}
                  expanded={expanded}
                  onToggle={toggle}
                  permissions={permissions}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SidebarPreview;
